<script setup lang="ts">
import VideoCallRoomsBoard from "~/components/video-call/VideoCallRoomsBoard.vue";
import VideoCallRoomsStatus from "~/components/video-call/VideoCallRoomsStatus.vue";
import { useVideoCallsStore } from "~/stores/videoCalls";
import type { VideoCallRoom } from "~/types/videoCalls";

const props = withDefaults(defineProps<{ compact?: boolean }>(), {
  compact: false,
});
const emit = defineEmits<{ "waiting-room": [] }>();
const store = useVideoCallsStore();
const titleId = useId();
const roomsButton = ref<HTMLButtonElement | null>(null);
const controls = ref<HTMLDivElement | null>(null);
const announcementBody = ref("");
const actionAnnouncement = ref("");
const actionError = ref("");
const localBusy = ref(false);
const snapshot = computed(() => store.roomsSnapshot);
const busy = computed(() => store.roomsLoading || localBusy.value);
const closing = computed(() => snapshot.value?.state === "closing");
const boardDisabled = computed(() => busy.value || closing.value);
const isHost = computed(() => store.role === "host");
const selfId = computed(() => store.connection?.participant.id);
const helpRequested = computed(() =>
  snapshot.value?.help_requests.some(
    (request) => request.participant_id === selfId.value,
  ),
);
const canOpenRooms = computed(() => {
  const current = snapshot.value;
  if (!current) return false;
  return (
    current.roster?.some((participant) => {
      const destination = current.planned_assignments?.[participant.id];
      return (
        participant.role === "guest" &&
        !["left", "removed"].includes(participant.state) &&
        Boolean(destination && destination !== current.main_room_id)
      );
    }) ?? false
  );
});
const canAddRoom = computed(
  () => (snapshot.value?.rooms.filter((room) => !room.is_main).length ?? 0) < 3,
);
const error = computed(() => store.roomsError || actionError.value);
const roomsButtonLabel = computed(() => {
  const count = snapshot.value?.help_requests.length ?? 0;
  return count
    ? `Abrir salas, ${count} ${count === 1 ? "pedido de ajuda" : "pedidos de ajuda"}`
    : "Abrir salas";
});

const execute = async (
  action: () => Promise<boolean>,
  successMessage: string,
): Promise<boolean> => {
  if (busy.value) return false;
  localBusy.value = true;
  actionError.value = "";
  actionAnnouncement.value = "";
  try {
    const success = await action();
    if (success) actionAnnouncement.value = successMessage;
    return success;
  } catch {
    actionError.value = "Não foi possível concluir. Tente novamente.";
    return false;
  } finally {
    localBusy.value = false;
  }
};

const openDialog = async (): Promise<void> => {
  if (props.compact) store.setPanelSize("medium");
  actionError.value = "";
  store.setRoomsOpen(true);
  await store.refreshRooms();
};

const closeDialog = async (): Promise<void> => {
  if (busy.value) return;
  store.setRoomsOpen(false);
  await nextTick();
  roomsButton.value?.focus();
};

const saveRooms = (
  rooms: VideoCallRoom[],
  assignments = { ...snapshot.value?.planned_assignments },
  successMessage = "Organização das salas salva.",
): Promise<boolean> =>
  execute(() => store.saveRooms({ rooms, assignments }), successMessage);

const addRoom = (): void => {
  const current = snapshot.value;
  if (!current || boardDisabled.value || !canAddRoom.value) return;
  const names = new Set(current.rooms.map((room) => room.name));
  let number = current.rooms.filter((room) => !room.is_main).length + 1;
  while (names.has(`Sala ${number}`)) number += 1;
  saveRooms([
    ...current.rooms,
    {
      id: crypto.randomUUID(),
      name: `Sala ${number}`,
      is_main: false,
      order: Math.max(...current.rooms.map((room) => room.order)) + 1,
    },
  ]);
};

const renameRoom = (roomId: string, name: string): void => {
  if (!snapshot.value || boardDisabled.value) return;
  saveRooms(
    snapshot.value.rooms.map((room) =>
      room.id === roomId ? { ...room, name } : { ...room },
    ),
  );
};

const removeRoom = (roomId: string): void => {
  const current = snapshot.value;
  if (!current || boardDisabled.value) return;
  const assignments = { ...current.planned_assignments };
  for (const [participantId, assignedRoom] of Object.entries(assignments)) {
    if (assignedRoom === roomId)
      assignments[participantId] = current.main_room_id;
  }
  saveRooms(
    current.rooms.filter((room) => room.id !== roomId),
    assignments,
    "Sala vazia excluída.",
  );
};

const moveParticipant = (participantId: string, roomId: string): void => {
  const current = snapshot.value;
  if (!current || boardDisabled.value) return;
  const participant = current.roster?.find(
    (entry) => entry.id === participantId,
  );
  const destination = current.rooms.find((room) => room.id === roomId);
  if (!participant || !destination) return;
  const successMessage = `${participant.name} ${
    current.state === "prepared" ? "foi colocado em" : "foi movido para"
  } ${destination.name}.`;
  if (current.state === "prepared") {
    saveRooms(
      current.rooms,
      { ...current.planned_assignments, [participantId]: roomId },
      successMessage,
    );
    return;
  }
  execute(() => store.moveToRoom(participantId, roomId), successMessage);
};

const enterRoom = (roomId: string): void => {
  if (!selfId.value || boardDisabled.value) return;
  const participantId = selfId.value;
  const room = snapshot.value?.rooms.find((entry) => entry.id === roomId);
  execute(
    () => store.moveToRoom(participantId, roomId),
    `Você entrou em ${room?.name ?? "outra sala"}.`,
  );
};

const openRooms = (): void => {
  execute(() => store.openRooms(), "As salas foram abertas.");
};

const closeRooms = (): void => {
  execute(
    () => store.closeRooms(),
    "Todos voltarão à sala principal em dez segundos.",
  );
};

const sendAnnouncement = async (): Promise<void> => {
  const body = announcementBody.value.trim();
  if (!body || body.length > 500) return;
  const success = await execute(
    () => store.sendRoomAnnouncement(body),
    "Aviso enviado a todas as salas.",
  );
  if (success) announcementBody.value = "";
};

const returnToMain = async (): Promise<void> => {
  const success = await execute(
    () => store.returnToMainRoom(),
    "Você voltou à sala principal.",
  );
  if (success) {
    await nextTick();
    controls.value?.focus();
  }
};

const toggleHelp = (): void => {
  if (helpRequested.value) {
    execute(() => store.cancelRoomHelp(), "Pedido de ajuda cancelado.");
  } else {
    execute(() => store.requestRoomHelp(), "O professor recebeu seu chamado.");
  }
};

const openWaitingRoom = async (): Promise<void> => {
  await closeDialog();
  emit("waiting-room");
};
</script>

<template>
  <div
    v-if="snapshot"
    ref="controls"
    class="rooms-controls"
    :class="{ 'rooms-controls--compact': props.compact }"
    role="group"
    aria-label="Controles de salas"
    tabindex="-1"
  >
    <button
      v-if="isHost"
      ref="roomsButton"
      class="rooms-controls__button"
      :class="{ 'rooms-controls__button--active': store.roomsOpen }"
      type="button"
      :aria-label="roomsButtonLabel"
      :title="roomsButtonLabel"
      :aria-expanded="store.roomsOpen"
      aria-haspopup="dialog"
      data-testid="video-call-rooms-control"
      @click="openDialog"
    >
      <i class="mdi mdi-view-grid-outline" aria-hidden="true" />
      <span class="rooms-controls__label">Salas</span>
      <span
        v-if="snapshot.help_requests.length"
        class="rooms-controls__badge"
        aria-hidden="true"
      >
        {{ snapshot.help_requests.length }}
      </span>
    </button>
    <template v-else>
      <button
        v-if="snapshot.current_room_id !== snapshot.main_room_id"
        class="rooms-controls__button"
        type="button"
        :disabled="busy"
        aria-label="Voltar à sala principal"
        title="Voltar à sala principal"
        data-testid="video-call-return-main-room"
        @click="returnToMain"
      >
        <i class="mdi mdi-home-import-outline" aria-hidden="true" />
        <span class="rooms-controls__label">Voltar à principal</span>
      </button>
      <button
        class="rooms-controls__button"
        :class="{ 'rooms-controls__button--called': helpRequested }"
        type="button"
        :disabled="busy || closing || snapshot.host_present"
        :aria-label="
          helpRequested
            ? 'Professor chamado. Cancelar pedido de ajuda'
            : snapshot.host_present
              ? 'O professor está nesta sala'
              : 'Chamar professor'
        "
        :title="
          helpRequested
            ? 'Professor chamado. Cancelar pedido de ajuda'
            : snapshot.host_present
              ? 'O professor está nesta sala'
              : 'Chamar professor'
        "
        :aria-pressed="Boolean(helpRequested)"
        data-testid="video-call-room-help"
        @click="toggleHelp"
      >
        <i
          class="mdi"
          :class="helpRequested ? 'mdi-hand-wave' : 'mdi-hand-wave-outline'"
          aria-hidden="true"
        />
        <span class="rooms-controls__label">
          {{
            helpRequested ? "Professor chamado · Cancelar" : "Chamar professor"
          }}
        </span>
      </button>
    </template>

    <v-dialog
      v-if="isHost"
      :model-value="store.roomsOpen"
      :persistent="busy"
      :z-index="2600"
      width="calc(100% - 24px)"
      max-width="1120"
      scrollable
      :aria-labelledby="titleId"
      data-testid="video-call-rooms-dialog"
      @update:model-value="!$event && closeDialog()"
    >
      <v-card class="rooms-dialog" rounded="xl">
        <v-card-title class="rooms-dialog__header">
          <div>
            <span :id="titleId">Salas</span>
            <p>
              {{
                snapshot.state === "prepared"
                  ? "Organize os alunos e abra as salas quando estiver pronto."
                  : snapshot.state === "closing"
                    ? "Todos estão voltando à sala principal."
                    : "Arraste para mover os alunos ou entre em uma sala para acompanhar."
              }}
            </p>
          </div>
          <button
            type="button"
            class="rooms-dialog__close"
            aria-label="Fechar salas"
            :disabled="busy"
            @click="closeDialog"
          >
            <i class="mdi mdi-close" aria-hidden="true" />
          </button>
        </v-card-title>

        <v-card-text class="rooms-dialog__body">
          <p v-if="error" class="rooms-dialog__error" role="alert">
            {{ error }}
          </p>
          <VideoCallRoomsStatus v-if="closing" hide-room />
          <div class="rooms-dialog__toolbar">
            <span class="rooms-dialog__state">
              <i
                class="mdi"
                :class="
                  snapshot.state === 'prepared'
                    ? 'mdi-pencil-outline'
                    : snapshot.state === 'closing'
                      ? 'mdi-timer-sand'
                      : 'mdi-circle-small'
                "
                aria-hidden="true"
              />
              {{
                snapshot.state === "prepared"
                  ? "Preparadas"
                  : snapshot.state === "closing"
                    ? "Voltando à principal"
                    : "Salas abertas"
              }}
            </span>
            <div class="rooms-dialog__toolbar-actions">
              <button
                type="button"
                class="rooms-dialog__outlined-button"
                :disabled="boardDisabled || !canAddRoom"
                @click="addRoom"
              >
                <i class="mdi mdi-plus" aria-hidden="true" /> Adicionar sala
              </button>
              <button
                type="button"
                class="rooms-dialog__outlined-button"
                :disabled="busy"
                @click="openWaitingRoom"
              >
                <i class="mdi mdi-account-clock-outline" aria-hidden="true" />
                Sala de espera
                <span
                  v-if="store.pendingJoinRequests.length"
                  class="rooms-dialog__waiting-count"
                >
                  {{ store.pendingJoinRequests.length }}
                </span>
              </button>
            </div>
          </div>

          <VideoCallRoomsBoard
            :snapshot="snapshot"
            :disabled="boardDisabled"
            :moving-participant-id="store.roomsMovingParticipantId"
            @rename="renameRoom"
            @remove="removeRoom"
            @move="moveParticipant"
            @enter="enterRoom"
          />

          <form
            class="rooms-dialog__announcement"
            @submit.prevent="sendAnnouncement"
          >
            <label :for="`${titleId}-announcement`"
              >Enviar aviso a todas as salas</label
            >
            <div class="rooms-dialog__announcement-input">
              <textarea
                :id="`${titleId}-announcement`"
                v-model="announcementBody"
                rows="2"
                maxlength="500"
                :disabled="boardDisabled"
                placeholder="Escreva uma orientação para todos…"
                :aria-describedby="`${titleId}-announcement-length`"
              />
              <button
                type="submit"
                class="rooms-dialog__outlined-button"
                :disabled="boardDisabled || !announcementBody.trim()"
              >
                <i class="mdi mdi-bullhorn-outline" aria-hidden="true" /> Enviar
                aviso
              </button>
            </div>
            <span
              :id="`${titleId}-announcement-length`"
              class="rooms-dialog__length"
            >
              {{ announcementBody.length }}/500 caracteres
            </span>
          </form>
          <p class="rooms-dialog__sr-only" aria-live="polite">
            {{ actionAnnouncement }}
          </p>
        </v-card-text>

        <v-card-actions class="rooms-dialog__footer">
          <span>{{
            busy
              ? "Salvando…"
              : snapshot.state === "prepared" && !canOpenRooms
                ? "Mova pelo menos um aluno para outra sala"
                : "Até quatro pessoas na chamada"
          }}</span>
          <button
            v-if="snapshot.state === 'prepared'"
            type="button"
            class="rooms-dialog__primary-button"
            :disabled="boardDisabled || !canOpenRooms"
            @click="openRooms"
          >
            <i class="mdi mdi-door-open" aria-hidden="true" /> Abrir salas
          </button>
          <button
            v-else
            type="button"
            class="rooms-dialog__primary-button"
            :disabled="boardDisabled"
            @click="closeRooms"
          >
            <i class="mdi mdi-home-import-outline" aria-hidden="true" />
            {{ closing ? "Trazendo todos de volta…" : "Trazer todos de volta" }}
          </button>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <p v-if="!store.roomsOpen" class="rooms-dialog__sr-only" aria-live="polite">
      {{ actionAnnouncement }}
    </p>
  </div>
</template>

<style scoped lang="scss">
.rooms-controls {
  display: flex;
  align-items: center;
  gap: 6px;

  &:focus-visible {
    border-radius: 13px;
    outline: 2px solid #3153ba;
    outline-offset: 3px;
  }
}

.rooms-controls__button {
  position: relative;
  display: inline-flex;
  min-width: 44px;
  height: 44px;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 0 12px;
  border-radius: 13px;
  background: #eef1f7;
  color: #273550;
  font: inherit;
  font-size: 0.76rem;
  font-weight: 700;
  cursor: pointer;
}

.rooms-controls__button--active {
  background: #dfe6ff;
  color: #2448d8;
}

.rooms-controls__button--called {
  background: #fff0d5;
  color: #865107;
}

.rooms-controls__badge {
  position: absolute;
  top: -5px;
  right: -5px;
  display: grid;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  place-items: center;
  border: 2px solid #fff;
  border-radius: 999px;
  background: #b76f0b;
  color: #fff;
  font-size: 0.62rem;
}

.rooms-controls--compact .rooms-controls__button {
  min-width: 32px;
  width: 32px;
  height: 36px;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.22);
  background: rgba(19, 28, 48, 0.78);
  color: #fff;
}

.rooms-controls--compact .rooms-controls__label {
  display: none;
}

.rooms-dialog {
  max-height: calc(100dvh - 24px);
  overflow: hidden;
  color: #26334d;
}

.rooms-dialog__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 22px 24px 14px;
  color: #17233c;
  font-size: 1.15rem;
  font-weight: 700;
  white-space: normal;

  p {
    margin: 5px 0 0;
    color: #66748d;
    font-size: 0.82rem;
    font-weight: 400;
    line-height: 1.5;
  }
}

.rooms-dialog__close {
  display: grid;
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  place-items: center;
  border-radius: 10px;
  background: #f0f3f9;
  color: #273550;
  cursor: pointer;
}

.rooms-dialog__body {
  padding: 0 24px 20px !important;
}

.rooms-dialog__error {
  margin: 0 0 14px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #fff0f1;
  color: #a51f34;
  font-size: 0.82rem;
}

.rooms-dialog__toolbar,
.rooms-dialog__toolbar-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.rooms-dialog__toolbar {
  justify-content: space-between;
  margin: 0 0 16px;
}

.rooms-dialog__state {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 8px;
  border-radius: 6px;
  background: #edf1ff;
  color: #3855a7;
  font-size: 0.75rem;
  font-weight: 700;
}

.rooms-dialog__outlined-button,
.rooms-dialog__primary-button {
  display: inline-flex;
  min-height: 40px;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 12px;
  border: 1px solid #ccd7f0;
  border-radius: 8px;
  color: #3153ba;
  font: inherit;
  font-size: 0.78rem;
  font-weight: 700;
  cursor: pointer;
}

.rooms-dialog__primary-button {
  border-color: #3156d3;
  background: #3156d3;
  color: #fff;
}

.rooms-dialog__waiting-count {
  min-width: 20px;
  padding: 1px 5px;
  border-radius: 5px;
  background: #fff0d5;
  color: #865107;
}

.rooms-dialog__announcement {
  margin: 22px 0 0;
  padding-top: 18px;
  border-top: 1px solid #e5eaf4;

  > label {
    display: block;
    margin-bottom: 8px;
    font-size: 0.82rem;
    font-weight: 700;
  }
}

.rooms-dialog__announcement-input {
  display: flex;
  align-items: center;
  gap: 10px;

  textarea {
    width: 100%;
    min-width: 0;
    min-height: 70px;
    flex: 1;
    padding: 10px 12px;
    resize: vertical;
    border: 1px solid #d6deed;
    border-radius: 8px;
    color: inherit;
    font: inherit;
    font-size: 0.82rem;
  }
}

.rooms-dialog__length {
  display: block;
  margin-top: 5px;
  color: #75829a;
  font-size: 0.7rem;
}

.rooms-dialog__footer {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 24px 20px;
  border-top: 1px solid #e5eaf4;

  > span {
    color: #66748d;
    font-size: 0.75rem;
  }
}

button:disabled,
textarea:disabled {
  cursor: default;
  opacity: 0.55;
}

button:focus-visible,
textarea:focus-visible {
  outline: 2px solid #3153ba;
  outline-offset: 2px;
}

.rooms-dialog__sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
}

@media (max-width: 760px) {
  .rooms-controls__button {
    width: 44px;
    padding: 0;
  }

  .rooms-controls__label {
    display: none;
  }
}

@media (max-width: 600px) {
  .rooms-dialog__header {
    padding: 18px 16px 14px;
  }

  .rooms-dialog__body {
    padding-right: 16px !important;
    padding-left: 16px !important;
  }

  .rooms-dialog__toolbar,
  .rooms-dialog__announcement-input {
    align-items: stretch;
    flex-direction: column;
  }

  .rooms-dialog__toolbar-actions {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .rooms-dialog__footer {
    align-items: stretch;
    flex-direction: column;
    padding: 14px 16px 18px;
  }
}
</style>
